import React, { useEffect, useState, useRef } from 'react'
import { DriverTVCard } from './DriverTVCard'
import styles from '../styles.module.css'
import { format } from 'date-fns'

import nextCargoLogo from '../assets/next-cargo-logo.svg'
import { DriverCalled, TV } from '../DriversTV'
import { useSpeechSynthesis } from '../../../hooks/useSpeechSynthesis'
import { useWindowReload } from '../../../hooks'
import { CompanyHostApi } from '../../../domain'
import { TVLogo, TVLogoType } from './TVLogo'
import { CountAttendance } from './CountAttendance'

interface Props {
  lastDriverCalled: DriverCalled | null
  tvList: TV[]
  attendeds: number
  company: CompanyHostApi | null

  refetchDriverList: () => Promise<void>
}

const DRIVER_CALL_TIMES = 3

export function DriverTVContent({
  lastDriverCalled,
  attendeds,
  tvList,
  company,
  refetchDriverList,
}: Props) {
  useWindowReload()

  const { talk } = useSpeechSynthesis()

  const [sortList, setSortList] = useState(false)
  const isCallingRef = useRef(false)
  const lastCalledIdRef = useRef<number | null>(null)
  const [currentDriverCalled, setCurrentDriverCalled] = useState<DriverCalled | null>(null)
  let panel = sortList
    ? tvList.filter(it => Number(it.driverTv.id) !== currentDriverCalled?.id)
    : currentDriverCalled
    ? tvList.filter(it => Number(it.driverTv.id) !== currentDriverCalled.id)
    : tvList

  const list = panel
  .sort(
    (a, b) =>
      new Date(b.driverTv.called_at).getTime() -
      new Date(a.driverTv.called_at).getTime()
  )
  .slice(0, 10)

  // Efeito para definir o motorista atual quando não está chamando
  useEffect(() => {
    if (!isCallingRef.current && list.length > 0 && !currentDriverCalled) {
      const lastDriver = list[list.length - 1]
      setCurrentDriverCalled(lastDriver.driverTv)
    }
  }, [list, currentDriverCalled])

  useEffect(() => {
    const handleDriverCall = async () => {
      if (!lastDriverCalled) {
        console.log("❌ DriverTVContent - Sem lastDriverCalled, não executando")
        return
      }

      if (isCallingRef.current) {
        console.log("⏳ DriverTVContent - Já está chamando, ignorando:", lastDriverCalled.queue?.driver?.name)
        return
      }

      // Se é o mesmo driver que já foi chamado, não chama novamente
      if (lastCalledIdRef.current === lastDriverCalled.id) {
        console.log("🔄 DriverTVContent - Mesmo driver já foi chamado:", lastDriverCalled.queue?.driver?.name)
        return
      }

      console.log("🔊 DriverTVContent - Iniciando chamada de voz para:", lastDriverCalled.queue?.driver?.name)
      setSortList(true)
      setCurrentDriverCalled(lastDriverCalled)

      // Marca que está chamando e salva o ID
      isCallingRef.current = true
      lastCalledIdRef.current = lastDriverCalled.id

      console.log("🎤 DriverTVContent - callDriver executado para:", lastDriverCalled.queue.driver.name)
      const { driver, truck } = lastDriverCalled.queue

      // Função auxiliar para pausar a execução por um período
      const delay = (ms: number) =>
        new Promise(resolve => setTimeout(resolve, ms))

      // Cria o áudio uma vez
      const audio = new Audio('./call-driver-tv.mp3')

      for (let i = 0; i < DRIVER_CALL_TIMES; i++) {
        console.log(`🔄 DriverTVContent - Chamada ${i + 1}/${DRIVER_CALL_TIMES} para:`, driver.name)
        try {
          // Tenta reproduzir o áudio
          await audio.play()
        } catch (error) {
          console.error('Erro ao reproduzir áudio', error)
        }

        // Aguarda 4 segundos após o áudio
        await delay(4000)

        const phrases = [
          `Chamando motorista ${driver.name}`,
          `com a placa ${truck.licensePlate}`
        ]

        for (const phrase of phrases) {
          console.log("🗣️ DriverTVContent - Falando:", phrase)
          talk(phrase)
        }

        // Aguarda 8 segundos antes de chamar novamente
        await delay(8000)
      }

      console.log("✅ DriverTVContent - Finalizou todas as 3 chamadas para:", driver.name)
      isCallingRef.current = false
    }

    handleDriverCall()
  }, [lastDriverCalled, talk])


  return (
    <div className={styles.content}>
      <section className={styles.next}>
        <div className={styles.images}>
          <img src={nextCargoLogo} alt='' />

          {company && (
            <TVLogo
              logo={company?.id.toString() as TVLogoType}
              alt={company?.name}
            />
          )}
        </div>

        <div className={styles.driver}>
          <p>Próximo</p>

          {currentDriverCalled && (
            <div className={styles.details}>
              <time>
                {format(
                  new Date(currentDriverCalled.called_at),
                  "dd/MM '•' HH'h'mm"
                )}
              </time>

              <div className={styles['truck-license-plate']}>
                {currentDriverCalled?.queue?.truck.licensePlate}
              </div>
              <span className={styles.name}>
                {currentDriverCalled?.queue?.driver.name}
              </span>
            </div>
          )}
        </div>
      </section>

      <section className={styles.summary}>
        <div className={styles.grid}>
          {list.map(item => (
            <DriverTVCard
              driverTv={item.driverTv}
              inAttendance={item.inAttendance}
              key={item.driverTv.id}
            />
          ))}
        </div>

        <CountAttendance
          attendeds={attendeds}
          refetchDriverList={refetchDriverList}
        />
      </section>
    </div>
  )
}
